import { useThemeMode } from "@/contexts/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { DarkMode, ExpandMore, Language, LightMode, MenuRounded } from "@mui/icons-material";
import { Box, Button, Container, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { SALE_LANGUAGE_OPTIONS } from "@/i18n/config";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import BackButton from "@/components/BackButton";
import useSwipeBack from "@/hooks/useSwipeBack";
import { LeadBackNavigation } from "../components/LeadBackNavigation";
import SecondAppSidebar from "../components/SecondAppSidebar";
import { isActivatedSalePage } from "@/utils/saleNavigation";

export default function SecondAppLayout() {
  const { mode, toggleTheme } = useThemeMode();
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [languageAnchor, setLanguageAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const activationEntry = isActivatedSalePage(pathname, search);
  const leadBackAction = useRef<(() => void) | null>(null);
  const registerLeadBack = useCallback((action: (() => void) | null) => { leadBackAction.current = action; }, []);
  const isLeadPage = /^\/(?:sale|sell)-support\/leads(?:\/|$)/.test(pathname);
  const isHome =
    pathname === "/sale-support" ||
    pathname === "/sale-support/" ||
    pathname === "/sell-support" ||
    pathname === "/sell-support/";
  const goBack = useCallback(() => {
    if (isHome || activationEntry) return;
    if (leadBackAction.current) {
      leadBackAction.current();
      return;
    }
    if (isLeadPage) {
      navigate(/\/leads(?:\/upcoming)?\/?$/.test(pathname) ? "/sale-support" : `/sale-support/leads${state?.leadListSearch ? `?${state.leadListSearch}` : ""}`, { replace: true });
      return;
    }
    if (/^\/(?:sale|sell)-support\/sales(?:\/(?:due|my))?\/?$/.test(pathname) || pathname.endsWith("/settings")) {
      navigate("/sale-support", { replace: true });
      return;
    }
    if (/^\/(?:sale|sell)-support\/sales\//.test(pathname)) {
      if (state?.saleListPath) navigate(state.saleListPath, { replace: true });
      else navigate("/sale-support/sales", { replace: true });
      return;
    }
    navigate("/sale-support", { replace: true });
  }, [isHome, activationEntry, isLeadPage, pathname, state, navigate]);

  useSwipeBack(goBack, isHome || activationEntry);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {isLeadPage && <BackButton onClick={goBack} />}
      {isHome && <Box component="header" sx={{ position: "sticky", top: 0, zIndex: (theme) => theme.zIndex.appBar, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper", mb: 1.5 }}>
      <Container maxWidth="lg" sx={{ px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <IconButton aria-label="Open navigation" onClick={() => setSidebarOpen(true)}><MenuRounded /></IconButton>
        <Typography variant="subtitle1" fontWeight={750} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          Sale Support
        </Typography>
        {isHome && <Stack direction="row" spacing={0.75} alignItems="center">
          <Button
            className="glass"
            aria-label={t("selectLanguage")}
            aria-haspopup="menu"
            aria-controls={languageAnchor ? "sell-support-language-menu" : undefined}
            aria-expanded={Boolean(languageAnchor)}
            onClick={(event) => setLanguageAnchor(event.currentTarget)}
            startIcon={<Language sx={{ color: "primary.main", fontSize: 18 }} />}
            endIcon={<ExpandMore sx={{ color: "text.secondary", fontSize: 18 }} />}
            sx={{ borderRadius: "10px", px: 1.5, py: 0.7, height: 40,
              color: "text.primary", fontWeight: 600, fontSize: "0.85rem", textTransform: "none" }}
          >
            {SALE_LANGUAGE_OPTIONS.find((language) => language.code === i18n.language)?.label || "English"}
          </Button>
          <Menu id="sell-support-language-menu" anchorEl={languageAnchor}
            open={Boolean(languageAnchor)} onClose={() => setLanguageAnchor(null)}
            PaperProps={{ sx: { borderRadius: "10px", mt: 1 } }}>
            {SALE_LANGUAGE_OPTIONS.map((language) => (
              <MenuItem key={language.code} selected={i18n.language === language.code}
                onClick={() => {
                  void i18n.changeLanguage(language.code);
                  setLanguageAnchor(null);
                }}>
                  {language.label}
              </MenuItem>
            ))}
          </Menu>
          <Tooltip title={t(mode === "dark" ? "lightMode" : "darkMode")}>
            <IconButton onClick={toggleTheme} aria-label={t("themeMode")} size="small"
              sx={{ color: "primary.main", bgcolor: "action.hover", width: 36, height: 36 }}>
              {mode === "dark" ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>}
      </Stack>
      </Container>
      </Box>}
      {!activationEntry && <SecondAppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
      <Container component="main" maxWidth={/^\/sale-support\/leads\/?$/.test(pathname) ? "lg" : "sm"} sx={{ px: 2, pb: 6, pt: isHome ? 1.5 : 0, flex: 1, display: "flex", flexDirection: "column" }}>
      <ErrorBoundary>
        <LeadBackNavigation.Provider value={registerLeadBack}><Outlet /></LeadBackNavigation.Provider>
      </ErrorBoundary>
    </Container>
    </Box>
  );
}
